import React, { useContext, useState, useMemo } from 'react';
import { TaskContext } from '../contexts/TaskContext';
import { 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableSortLabel, 
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Toolbar,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { Delete, DeleteOutline, Add, CheckCircle, RemoveCircle } from '@mui/icons-material';

const TodoPage = () => {
  const { tasks, addTask, deleteTask, bulkDeleteTasks, bulkCompleteTasks, bulkRemoveTasks, updateTaskName, updateTaskStatus } = useContext(TaskContext);
  const [currentTab, setCurrentTab] = useState(0);
  const [orderBy, setOrderBy] = useState('addedDatetime');
  const [order, setOrder] = useState('desc');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSelectedTasks([]);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTasks(filteredTasks.map(task => task.id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleBulkDelete = () => {
    bulkDeleteTasks(selectedTasks);
    setSelectedTasks([]);
  };

  const handleBulkComplete = () => {
    bulkCompleteTasks(selectedTasks);
    setSelectedTasks([]);
  };

  const handleBulkRemove = () => {
    bulkRemoveTasks(selectedTasks);
    setSelectedTasks([]);
  };

  const handleSingleDelete = (taskId) => {
    deleteTask(taskId);
  };

  const handleAddTask = () => {
    if (newTaskName.trim()) {
      addTask(newTaskName.trim());
      setNewTaskName('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleAddTask();
    }
  };

  const handleStartEditTask = (task) => {
    setEditingTask(task.id);
    setEditingTaskName(task.name);
  };

  const handleSaveTaskName = () => {
    if (editingTaskName.trim() && editingTask) {
      updateTaskName(editingTask, editingTaskName.trim());
    }
    setEditingTask(null);
    setEditingTaskName('');
  };

  const handleCancelEditTask = () => {
    setEditingTask(null);
    setEditingTaskName('');
  };

  const handleTaskNameKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSaveTaskName();
    } else if (event.key === 'Escape') {
      handleCancelEditTask();
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    updateTaskStatus(taskId, newStatus);
    setEditingStatus(null);
  };

  const handleStatusClick = (taskId) => {
    setEditingStatus(taskId);
  };

  const handleStatusClose = () => {
    setEditingStatus(null);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  const getTaskStatus = (task) => {
    if (task.removed) return 'Removed';
    if (task.completed) return 'Completed';
    if (task.startDatetime) return 'Started';
    return 'New';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Started': return 'warning';
      case 'New': return 'default';
      case 'Removed': return 'error';
      default: return 'default';
    }
  };

  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    switch (currentTab) {
      case 1: // Completed
        filtered = tasks.filter(task => task.completed && !task.removed);
        break;
      case 2: // Started
        filtered = tasks.filter(task => task.startDatetime && !task.completed && !task.removed);
        break;
      case 3: // New
        filtered = tasks.filter(task => !task.startDatetime && !task.completed && !task.removed);
        break;
      case 4: // Removed
        filtered = tasks.filter(task => task.removed);
        break;
      default: // All
        filtered = tasks.filter(task => !task.removed);
    }

    return filtered.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle null values
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return order === 'asc' ? 1 : -1;
      if (bVal === null) return order === 'asc' ? -1 : 1;

      // Convert to comparable values
      if (orderBy.includes('Datetime')) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (order === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [tasks, currentTab, orderBy, order]);

  const columns = [
    { id: 'select', label: '', sortable: false },
    { id: 'name', label: 'Task Name', sortable: true },
    { id: 'status', label: 'Status', sortable: false },
    { id: 'sortOrder', label: 'Order', sortable: true },
    { id: 'startDatetime', label: 'Start Datetime', sortable: true },
    { id: 'finishDatetime', label: 'Finish Datetime', sortable: true },
    { id: 'addedDatetime', label: 'Added Datetime', sortable: true },
    { id: 'actions', label: '', sortable: false },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Todo Management
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Add new task..."
          value={newTaskName}
          onChange={(e) => setNewTaskName(e.target.value)}
          onKeyPress={handleKeyPress}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  onClick={handleAddTask}
                  disabled={!newTaskName.trim()}
                  color="primary"
                >
                  <Add />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label={`All (${tasks.filter(t => !t.removed).length})`} />
          <Tab label={`Completed (${tasks.filter(t => t.completed && !t.removed).length})`} />
          <Tab label={`Started (${tasks.filter(t => t.startDatetime && !t.completed && !t.removed).length})`} />
          <Tab label={`New (${tasks.filter(t => !t.startDatetime && !t.completed && !t.removed).length})`} />
          <Tab label={`Removed (${tasks.filter(t => t.removed).length})`} />
        </Tabs>
      </Paper>

      {selectedTasks.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: '1 1 100%' }}>
              {selectedTasks.length} selected
            </Typography>
            <Button
              color="error"
              startIcon={<Delete />}
              onClick={handleBulkDelete}
              sx={{ mr: 2, px: 3, py: 1 }}
            >
              Delete
            </Button>
            <Button
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleBulkComplete}
              sx={{ mr: 2, px: 3, py: 1 }}
            >
              Complete
            </Button>
            <Button
              color="warning"
              startIcon={<RemoveCircle />}
              onClick={handleBulkRemove}
              sx={{ px: 3, py: 1 }}
            >
              Remove
            </Button>
          </Toolbar>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id}>
                  {column.id === 'select' ? (
                    <Checkbox
                      indeterminate={selectedTasks.length > 0 && selectedTasks.length < filteredTasks.length}
                      checked={filteredTasks.length > 0 && selectedTasks.length === filteredTasks.length}
                      onChange={handleSelectAll}
                    />
                  ) : column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tasks found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTasks.map((task) => {
                const status = getTaskStatus(task);
                const isSelected = selectedTasks.includes(task.id);
                const isHovered = hoveredTask === task.id;
                
                return (
                  <TableRow 
                    key={task.id} 
                    hover
                    selected={isSelected}
                    onMouseEnter={() => setHoveredTask(task.id)}
                    onMouseLeave={() => setHoveredTask(null)}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectTask(task.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {editingTask === task.id ? (
                          <TextField
                            value={editingTaskName}
                            onChange={(e) => setEditingTaskName(e.target.value)}
                            onKeyDown={handleTaskNameKeyPress}
                            onBlur={handleSaveTaskName}
                            autoFocus
                            size="small"
                            sx={{ minWidth: 200 }}
                          />
                        ) : (
                          <Typography
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { 
                                backgroundColor: 'action.hover',
                                borderRadius: 1,
                                px: 1
                              }
                            }}
                            onClick={() => handleStartEditTask(task)}
                          >
                            {task.name}
                          </Typography>
                        )}
                        {isHovered && editingTask !== task.id && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleSingleDelete(task.id)}
                            sx={{ opacity: 0.7 }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {editingStatus === task.id ? (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                            value={status}
                            onChange={(e) => handleStatusChange(task.id, e.target.value)}
                            onClose={handleStatusClose}
                            open={true}
                            displayEmpty
                            variant="outlined"
                            MenuProps={{
                              onClose: handleStatusClose,
                            }}
                            sx={{
                              '& .MuiSelect-select': {
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center'
                              }
                            }}
                            renderValue={(value) => (
                              <Chip 
                                label={value} 
                                size="small" 
                                color={getStatusColor(value)}
                                variant="outlined"
                              />
                            )}
                          >
                            <MenuItem value="New">
                              <Chip label="New" size="small" color="default" variant="outlined" />
                            </MenuItem>
                            <MenuItem value="Started">
                              <Chip label="Started" size="small" color="warning" variant="outlined" />
                            </MenuItem>
                            <MenuItem value="Completed">
                              <Chip label="Completed" size="small" color="success" variant="outlined" />
                            </MenuItem>
                            <MenuItem value="Removed">
                              <Chip label="Removed" size="small" color="error" variant="outlined" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip 
                          label={status} 
                          size="small" 
                          color={getStatusColor(status)}
                          variant="outlined"
                          onClick={() => handleStatusClick(task.id)}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{task.sortOrder ?? 0}</TableCell>
                    <TableCell>{formatDateTime(task.startDatetime)}</TableCell>
                    <TableCell>{formatDateTime(task.finishDatetime)}</TableCell>
                    <TableCell>{formatDateTime(task.addedDatetime)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TodoPage;